import { type RefObject, useEffect, useRef } from 'react';

interface Options {
  /** ms of input quiescence required before we snap. Default 1500. */
  readonly idleMs?: number;
  /** Disables the hook (e.g. when the scroll container isn't rendered). */
  readonly enabled?: boolean;
}

/**
 * Auto-snaps a horizontally-scrolling container back to `scrollLeft = 0`
 * whenever the leader (front-most item key) changes, IF the user isn't
 * actively interacting with the container.
 *
 * "Active" = a pointerdown / touchstart / wheel / keydown landed on the
 * container within the last `idleMs`. We deliberately ignore native `scroll`
 * events: CSS snap and our own `scrollTo` both fire them, and reading them
 * as "user input" would create a livelock where every programmatic snap
 * resets the idle clock.
 */
export function useAutoScrollToLeader(
  scrollRef: RefObject<HTMLElement | null>,
  leaderKey: number | string,
  options?: Options,
): void {
  const { idleMs = 1500, enabled = true } = options ?? {};
  const lastInteractionRef = useRef<number>(0);
  const prevLeaderRef = useRef<number | string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const el = scrollRef.current;
    if (el === null) return;
    const mark = (): void => {
      lastInteractionRef.current = performance.now();
    };
    el.addEventListener('pointerdown', mark, { passive: true });
    el.addEventListener('touchstart', mark, { passive: true });
    el.addEventListener('wheel', mark, { passive: true });
    el.addEventListener('keydown', mark);
    return () => {
      el.removeEventListener('pointerdown', mark);
      el.removeEventListener('touchstart', mark);
      el.removeEventListener('wheel', mark);
      el.removeEventListener('keydown', mark);
    };
  }, [enabled, scrollRef]);

  useEffect(() => {
    if (!enabled) return;
    if (prevLeaderRef.current === null) {
      prevLeaderRef.current = leaderKey;
      return;
    }
    if (prevLeaderRef.current === leaderKey) return;
    prevLeaderRef.current = leaderKey;

    const el = scrollRef.current;
    if (el === null || el.scrollLeft === 0) return;
    return snapAfterIdle(scrollRef, lastInteractionRef, idleMs);
  }, [leaderKey, enabled, idleMs, scrollRef]);
}

function snapAfterIdle(
  scrollRef: RefObject<HTMLElement | null>,
  lastInteractionRef: RefObject<number>,
  idleMs: number,
): () => void {
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const trySnap = (): void => {
    const target = scrollRef.current;
    if (cancelled || target === null) return;
    const sinceInteraction = performance.now() - lastInteractionRef.current;
    if (sinceInteraction >= idleMs) {
      target.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }
    timer = setTimeout(trySnap, idleMs - sinceInteraction);
  };
  trySnap();
  return (): void => {
    cancelled = true;
    if (timer !== null) clearTimeout(timer);
  };
}
